import { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase';
import { collection, addDoc, serverTimestamp, doc, updateDoc, query, orderBy, limit, getDocs, where } from "firebase/firestore";
import toast from 'react-hot-toast';

const getInitialState = () => ({
    numeroPresupuesto: 'Calculando...',
    fechaPresupuesto: new Date().toISOString().split('T')[0], 
    datosCliente: { name: '', nif: '', direccion: '', email: '', telefono: '', codPostal: '', poblacion: '' },
    clienteSeleccionado: null,
    bloquesPresupuesto: [
        { id: Math.random(), descripcion: '', abierta: true, lineas: [] }
    ], 
    comisionVendedor: 10,
    costoFlete: 0, 
    costoInstalacion: 0, 
    descuentoPorcentaje: 0,
    transporteManualResumen: 0, 
    condicionesSeleccionadas: [
        "Garantia de 2 años sobre la fabricacion",
        "Presupuesto valido por 15 dias"
    ],
    tiempoEntrega: { cantidad: '2', unidad: 'semanas' },
    estado: 'Borrador',
    pedidoVinculado: null,
    notasTallerGlobal: '', // 🔥 JARVIS: INYECTADO ESTADO INICIAL
});

function createNewLine(type = 'db') {
    const baseLine = { id: Math.random(), cantidad: 1, type, notasTaller: '' }; // 🔥 JARVIS: INYECTADO ESTADO INICIAL
    if (type === 'db') {
        return { ...baseLine, producto: null, costo: 0, total: 0, materiales: 0, manoObra: 0, horas: 0, gastosFijosUnitario: 0, departamento: 'C' };
    }
    return { ...baseLine, descripcion: '', costo: 0, total: 0, materiales: 0, manoObra: 0, horas: 0, gastosFijosUnitario: 0, departamento: 'C' };
}

export function usePresupuestoForm(clientesDB, productosDB, optionsClientes) {
    const [state, setState] = useState(getInitialState());
    const [editingId, setEditingId] = useState(null);

    const generarSiguienteNumero = useCallback(async () => {
        const year = new Date().getFullYear();
        const prefix = `P-${year}-`;
        const q = query(collection(db, 'presupuestos'), where('numero', '>=', prefix), where('numero', '<', `P-${year + 1}-`), orderBy('numero', 'desc'), limit(1));
        try {
            const querySnapshot = await getDocs(q);
            let nextNum = 1;
            if (!querySnapshot.empty) {
                const lastNumStr = querySnapshot.docs[0].data().numero || `P-${year}-000`;
                const lastNum = parseInt(lastNumStr.split('-')[2], 10);
                nextNum = lastNum + 1;
            }
            const newNumero = `${prefix}${String(nextNum).padStart(3, '0')}`;
            setState(s => ({ ...s, numeroPresupuesto: newNumero }));
        } catch (error) {
            console.error("Error generando número:", error);
            setState(s => ({ ...s, numeroPresupuesto: 'Error' }));
        }
    }, []);

    useEffect(() => { 
        if (!editingId) {
            generarSiguienteNumero();
        } 
    }, [editingId, generarSiguienteNumero]);

    // ⚡ EL BYPASS FINANCIERO: Cálculo real basado en horas y prorrateo vivo
    const calculations = useMemo(() => {
        const comisionPct = parseFloat(state.comisionVendedor) || 0;
        const descuentoPct = parseFloat(state.descuentoPorcentaje) || 0;
        const costoFlete = parseFloat(state.costoFlete) || 0;
        const costoInstalacion = parseFloat(state.costoInstalacion) || 0; 
        const transporteManual = parseFloat(state.transporteManualResumen) || 0; 
        
        const todasLasLineas = (state.bloquesPresupuesto || []).reduce((acc, b) => [...acc, ...(b.lineas || [])], []);

        const lineasReales = todasLasLineas.filter(line => {
            const prodStr = typeof line.producto === 'string' ? line.producto : (line.producto?.label || "");
            const desc = (prodStr || line.descripcion || line.description || "").toLowerCase();
            return desc.trim() !== "" && !desc.includes("incluida") && !desc.includes("cliente") && !desc.includes("semanas");
        });

        // ⏱️ AUDITORÍA DE TIEMPOS: Sumatoria de horas de todo el presupuesto
        const totalHorasFabricacion = lineasReales.reduce((acc, line) => {
            return acc + ((parseFloat(line.horas) || 0) * (parseFloat(line.cantidad) || 0));
        }, 0);

        // Ya no dividimos entre 4. GastosFijosUnitario ya viene calculado al milímetro por las horas del producto
        const totalGastosFijos = lineasReales.reduce((acc, line) => {
            return acc + ((parseFloat(line.gastosFijosUnitario) || 0) * (parseFloat(line.cantidad) || 0));
        }, 0);

        const subtotalProductos = lineasReales.reduce((acc, line) => acc + (parseFloat(line.total) || 0), 0);
        
        const costes = lineasReales.reduce((acc, line) => {
            acc.materiales += (parseFloat(line.materiales) || 0) * (parseFloat(line.cantidad) || 0);
            const moTotalLinea = (parseFloat(line.manoObra) || 0) * (parseFloat(line.cantidad) || 0);
            const depto = line.departamento ? line.departamento.toString().toUpperCase().trim() : 'C';

            if (depto === 'T' || depto.includes('TAPICERIA')) {
                acc.manoObraTapiceria += moTotalLinea;
            } else {
                acc.manoObraCarpinteria += moTotalLinea;
            }
            return acc;
        }, { materiales: 0, manoObraCarpinteria: 0, manoObraTapiceria: 0 });

        const manoObraTotal = costes.manoObraCarpinteria + costes.manoObraTapiceria;
        const importeComision = (subtotalProductos * comisionPct) / 100;
        
        const subtotalParaPDF = subtotalProductos + importeComision + costoFlete + costoInstalacion;
        const baseImponible = subtotalParaPDF + transporteManual;

        const egreso = costes.materiales + manoObraTotal + importeComision + totalGastosFijos + costoFlete + costoInstalacion + transporteManual;
        const restante = baseImponible - egreso; 

        const descuentoImporte = baseImponible * (descuentoPct / 100);
        const baseConDescuento = baseImponible - descuentoImporte;
        const iva = baseConDescuento * 0.21;
        const totalGeneral = baseConDescuento + iva;

        let desglosePago = null;
        const condiciones = state.condicionesSeleccionadas || [];
        
        const es7030 = condiciones.some(c => typeof c === 'string' && (c.includes("70%") || c.includes("70/30")));
        const es5050 = condiciones.some(c => typeof c === 'string' && (c.includes("50%") || c.includes("50/50")));

        if (es7030) {
            desglosePago = { tipo: '70/30', anticipoPct: 70, anticipoMonto: totalGeneral * 0.70, entregaPct: 30, entregaMonto: totalGeneral * 0.30 };
        } else if (es5050) {
            desglosePago = { tipo: '50/50', anticipoPct: 50, anticipoMonto: totalGeneral * 0.50, entregaPct: 50, entregaMonto: totalGeneral * 0.50 };
        }

        return {
            totales: { 
                total: baseImponible, subtotalParaPDF, iva, materiales: costes.materiales, 
                manoObra: manoObraTotal, manoObraCarpinteria: costes.manoObraCarpinteria, manoObraTapiceria: costes.manoObraTapiceria,
                totalHoras: totalHorasFabricacion // Exportamos las horas para la auditoría
            },
            importeComision, gastosFijos: totalGastosFijos, costoFlete, costoInstalacion, 
            transporteManualResumen: transporteManual, descuentoImporte, egreso, restante, totalGeneral, desglosePago 
        };
    }, [state]);

    const resetForm = useCallback(() => {
        setState(getInitialState()); 
        setEditingId(null);
        generarSiguienteNumero();
    }, [generarSiguienteNumero]);

    const handleSelectCliente = (selectedOption) => {
        if (selectedOption) {
            const cliente = clientesDB.find(c => c.id === selectedOption.value);
            if (cliente) {
                const primerContacto = (cliente.personasContacto && cliente.personasContacto.length > 0) ? cliente.personasContacto[0] : {};
                setState(s => ({ 
                    ...s, 
                    clienteSeleccionado: selectedOption, 
                    datosCliente: { 
                        name: cliente.name || '', nif: cliente.nif || '', direccion: cliente.direccion || '', 
                        email: cliente.email || primerContacto.email || '', telefono: cliente.telefono || primerContacto.telefono || '', 
                        codPostal: cliente.codPostal || '', poblacion: cliente.poblacion || '' 
                    } 
                }));
            }
        } else { 
            const freshInitial = getInitialState();
            setState(s => ({ ...s, clienteSeleccionado: null, datosCliente: freshInitial.datosCliente })); 
        }
    };

    const handleClienteChange = (e) => { 
        const { name, value } = e.target; 
        setState(s => ({ ...s, datosCliente: { ...s.datosCliente, [name]: value } })); 
    };

    const handleLineChange = (bloqueId, lineId, field, value) => {
        setState(s => {
            const nuevosBloques = s.bloquesPresupuesto.map(b => {
                if (b.id === bloqueId) {
                    const newLineas = b.lineas.map(line => {
                        if (line.id === lineId) {
                            const updatedLine = { ...line, [field]: value };
                            if (field === 'producto' && value) {
                                const productoData = productosDB.find(p => p.producto === value);
                                if (productoData) {
                                    // ⚡ CÁLCULO EN VIVO: Extraemos la data inyectada de Firebase
                                    const horasFab = parseFloat(productoData.horas) || 0;
                                    const costoManoObraHora = 15.84; // Costo base 12€ + 32% SS
                                    const gastoFijoHora = parseFloat(productoData.gastoFijoHora) || 11.62;
                                    
                                    const costoMO = horasFab * costoManoObraHora;
                                    const costoGF = horasFab * gastoFijoHora;
                                    const costoMat = parseFloat(productoData.materiales) || 0;
                                    
                                    const costoTotalEmpresa = costoMat + costoMO + costoGF;
                                    const pvpSugerido = costoTotalEmpresa / (1 - 0.20); // Margen 20%

                                    updatedLine.horas = horasFab;
                                    updatedLine.materiales = costoMat;
                                    updatedLine.manoObra = costoMO;
                                    updatedLine.gastosFijosUnitario = costoGF;
                                    updatedLine.departamento = productoData.departamento || 'C';
                                    updatedLine.costo = pvpSugerido; // Establecemos el PVP sugerido
                                }
                            }
                            updatedLine.total = (parseFloat(updatedLine.costo) || 0) * (parseFloat(updatedLine.cantidad) || 0);
                            return updatedLine;
                        }
                        return line;
                    });
                    return { ...b, lineas: newLineas };
                }
                return b;
            });
            return { ...s, bloquesPresupuesto: nuevosBloques };
        });
    };

    const addDbLine = (bloqueId) => setState(s => ({
        ...s,
        bloquesPresupuesto: s.bloquesPresupuesto.map(b => 
            b.id === bloqueId ? { ...b, lineas: [...b.lineas, createNewLine('db')] } : b
        )
    }));

    const addManualLine = (bloqueId) => setState(s => ({
        ...s,
        bloquesPresupuesto: s.bloquesPresupuesto.map(b => 
            b.id === bloqueId ? { ...b, lineas: [...b.lineas, createNewLine('manual')] } : b
        )
    }));

    const removeLine = (bloqueId, lineId) => setState(s => ({
        ...s,
        bloquesPresupuesto: s.bloquesPresupuesto.map(b => 
            b.id === bloqueId ? { ...b, lineas: b.lineas.filter(l => l.id !== lineId) } : b
        )
    }));

    const handleCondicionesChange = (condicion) => { 
        setState(prevState => { 
            const ya = prevState.condicionesSeleccionadas.includes(condicion); 
            return { ...prevState, condicionesSeleccionadas: ya ? prevState.condicionesSeleccionadas.filter(c => c !== condicion) : [...prevState.condicionesSeleccionadas, condicion] }; 
        }); 
    };
    
    const handleSaveBudget = async () => {
        if (!state.datosCliente.name) return toast.error("Falta nombre cliente.");
        
        const condicionesLimpias = state.condicionesSeleccionadas.filter(c => 
            !c.toLowerCase().includes("plazo de entrega") && 
            !c.toLowerCase().includes("semanas") &&
            !c.toLowerCase().includes("forma de pago")
        );

        const bloquesEstructurados = state.bloquesPresupuesto.map(({ id, abierta, ...rest }) => ({
            ...rest,
            lineas: rest.lineas.map(({ id, ...lineRest }) => {
                const prodVal = typeof lineRest.producto === 'string' ? lineRest.producto : (lineRest.producto?.label || lineRest.descripcion || lineRest.description || '');
                return {
                    ...lineRest,
                    producto: prodVal
                };
            })
        }));

        const descripcionPlanaBackup = state.bloquesPresupuesto.map(b => b.descripcion).filter(Boolean).join('\n\n');
        const lineasPlanasBackup = state.bloquesPresupuesto.reduce((acc, b) => [...acc, ...b.lineas], []).map(({ id, ...rest }) => {
            const prodVal = typeof rest.producto === 'string' ? rest.producto : (rest.producto?.label || rest.descripcion || rest.description || '');
            return {
                ...rest,
                producto: prodVal
            };
        });

        const presupuestoData = {
            numero: state.numeroPresupuesto,
            fecha: state.fechaPresupuesto,
            cliente: state.datosCliente,
            bloques: bloquesEstructurados, 
            lineas: lineasPlanasBackup, 
            descripcion: descripcionPlanaBackup,
            notasTallerGlobal: state.notasTallerGlobal || '', // 🔥 JARVIS: GUARDANDO EN FIREBASE
            condicionesSeleccionadas: condicionesLimpias,
            tiempoEntrega: state.tiempoEntrega,
            calculos: { 
                ...calculations, 
                comisionVendedor: state.comisionVendedor, 
                costoFlete: state.costoFlete, 
                costoInstalacion: state.costoInstalacion, 
                transporteManualResumen: state.transporteManualResumen, 
                descuentoPorcentaje: state.descuentoPorcentaje 
            },
            totales: calculations.totales,
            estado: state.estado,
            clienteId: state.clienteSeleccionado?.value || null,
            updatedAt: serverTimestamp(),
        };

        try {
            if (editingId) {
                await updateDoc(doc(db, "presupuestos", editingId), presupuestoData);
                toast.success("¡Presupuesto actualizado!");
            } else {
                presupuestoData.createdAt = serverTimestamp();
                await addDoc(collection(db, "presupuestos"), presupuestoData);
                toast.success("¡Presupuesto creado!");
            }
            resetForm();
        } catch (e) {
            console.error("Error al guardar:", e);
            toast.error("Error al guardar.");
        }
    };
    
    const handleEditarPresupuesto = (presupuesto, isCopy = false) => {
        const condicionesFiltradas = (presupuesto.condicionesSeleccionadas || []).filter(c => 
            !c.toLowerCase().includes("plazo de entrega") && 
            !c.toLowerCase().includes("semanas")
        );

        const freshInitial = getInitialState();
        
        let bloquesMigrados = [];
        if (presupuesto.bloques && presupuesto.bloques.length > 0) {
            bloquesMigrados = presupuesto.bloques.map(b => ({
                id: Math.random(),
                nombre: b.nombre || '',
                descripcion: b.descripcion || '',
                abierta: true,
                lineas: (b.lineas || []).map(l => {
                    const prodValue = typeof l.producto === 'string' ? l.producto : (l.producto?.value || l.producto?.label || '');
                    return {
                        ...l,
                        id: Math.random(),
                        producto: prodValue,
                        type: prodValue ? 'db' : 'manual',
                        descripcion: l.descripcion || l.description || '',
                        notasTaller: l.notasTaller || '' // 🔥 JARVIS: RECUPERANDO NOTAS DE LINEAS
                    };
                })
            }));
        } else if (presupuesto.lineas && presupuesto.lineas.length > 0) {
            bloquesMigrados = [{
                id: Math.random(),
                nombre: 'Fase Principal',
                descripcion: presupuesto.descripcion || 'Descripción del Proyecto',
                abierta: true,
                lineas: presupuesto.lineas.map(l => {
                    const prodValue = typeof l.producto === 'string' ? l.producto : (l.producto?.value || l.producto?.label || '');
                    return {
                        ...l,
                        id: Math.random(),
                        producto: prodValue,
                        type: prodValue ? 'db' : 'manual',
                        descripcion: l.descripcion || l.description || '',
                        notasTaller: l.notasTaller || '' // 🔥 JARVIS: RECUPERANDO NOTAS DE LINEAS
                    };
                })
            }];
        } else {
            bloquesMigrados = freshInitial.bloquesPresupuesto;
        }

        const datosCargados = {
            ...freshInitial,
            ...presupuesto,
            numeroPresupuesto: isCopy ? 'Calculando...' : presupuesto.numero,
            fechaPresupuesto: isCopy ? freshInitial.fechaPresupuesto : presupuesto.fecha,
            datosCliente: presupuesto.cliente || presupuesto.datosCliente || freshInitial.datosCliente,
            
            comisionVendedor: presupuesto.calculos?.comisionVendedor || presupuesto.calculos?.comision || presupuesto.comisionVendedor || 10,
            costoFlete: presupuesto.calculos?.costoFlete || presupuesto.costoFlete || 0,
            costoInstalacion: presupuesto.calculos?.costoInstalacion || presupuesto.costoInstalacion || 0,
            descuentoPorcentaje: presupuesto.calculos?.descuentoPorcentaje || presupuesto.descuentoPorcentaje || 0,
            transporteManualResumen: presupuesto.calculos?.transporteManualResumen || presupuesto.transporteManualResumen || 0,

            condicionesSeleccionadas: condicionesFiltradas,
            tiempoEntrega: presupuesto.tiempoEntrega || { cantidad: '2', unidad: 'semanas' },
            bloquesPresupuesto: bloquesMigrados,
            notasTallerGlobal: presupuesto.notasTallerGlobal || '' // 🔥 JARVIS: RECUPERANDO NOTA GLOBAL
        };

        setState(datosCargados);
        
        if (isCopy) {
            setEditingId(null);
            generarSiguienteNumero();
        } else {
            setEditingId(presupuesto.id);
        }
        
        window.scrollTo(0, 0);
    };

    return { 
        state, setState, editingId, setEditingId, calculations, resetForm, 
        handleSelectCliente, handleClienteChange, handleLineChange, 
        addDbLine, addManualLine, removeLine, 
        handleSaveBudget, handleEditarPresupuesto, handleCondicionesChange, generarSiguienteNumero 
    };
}